import { useEffect, useState } from 'react';

import { useConfigContext } from 'lib/core/config';

import { useRequirementsContext } from 'lib/common/contexts/RequirementsContext';

import { useLocalStorage } from 'lib/common/hooks/useLocalStorage';

import Text from 'lib/common/components/Text';
import WarningBanner from 'lib/common/components/WarningBanner';

import { HELP_LINKS } from 'lib/common/constants/help';
import { LOCAL_STORAGE_KEYS } from 'lib/common/constants/localStorageKeys';

import appInIframe from 'lib/common/utils/appInIframe';

import styles from './notification-banner.module.scss';

const BANNER_DISPLAY_TIMEOUT_MS = 2000;

export default function NotificationBanner({ className }: { className?: string }) {
  const { config } = useConfigContext();
  const { isPermissionGranted } = useRequirementsContext();

  const [bannerVisible, setBannerVisible] = useState(false);
  const { getStorageItem, setStorageItem } = useLocalStorage();

  const getHideNotificationBannerValue = () => Boolean(getStorageItem(LOCAL_STORAGE_KEYS.HIDE_NOTIFICATION_BANNER));

  const hideBanner = () => {
    setStorageItem(LOCAL_STORAGE_KEYS.HIDE_NOTIFICATION_BANNER, 'true');
    setBannerVisible(false);
  };

  const [userHasHiddenBanner, setUserHasHiddenBanner] = useState(getHideNotificationBannerValue());

  const shouldNeverDisplayBanner = !appInIframe() || userHasHiddenBanner || isPermissionGranted('notifications', true);

  const setHiddenBanner = () => {
    setUserHasHiddenBanner(getHideNotificationBannerValue());
  };

  useEffect(() => {
    window.addEventListener('storage', setHiddenBanner, false);

    if (shouldNeverDisplayBanner) {
      return;
    }

    const timeout = setTimeout(() => {
      setBannerVisible(true);
    }, BANNER_DISPLAY_TIMEOUT_MS);

    return () => {
      clearTimeout(timeout);

      window.removeEventListener('storage', setHiddenBanner);
    };
  }, [shouldNeverDisplayBanner]);

  if (shouldNeverDisplayBanner || !bannerVisible) {
    return null;
  }

  return (
    <WarningBanner
      data-testid="notification-banner"
      className={className}
      onClose={hideBanner}
      body={
        <Text className={styles['notification-banner__text']}>
          Notifications aren’t currently enabled. You’ll need to{' '}
          <Text medium color="black" type="link" href={window.location.origin}>
            open {config.BRAND.productName} directly
          </Text>{' '}
          and enable notifications.{' '}
          <Text medium color="black" type="link" href={`${config.BRAND.helpDocsUrl}${HELP_LINKS.ENABLE_NOTIFICATIONS}`}>
            Why?
          </Text>
          .
        </Text>
      }
    />
  );
}
